// src/components/admin/ReportsAIAgent.jsx
// "Reports AI" page under Reports Management: the admin types an instruction ("Set a $30,000 target for December",
// "Which customers haven't visited in 3 months? Draft a New Year e-mail to them") and the agent carries it out.
// Changing revenue goals and exporting customers' contact details come back as approval cards that only run when the
// admin presses Approve. Campaign drafts open the Campaigns page pre-filled; the agent never sends anything.
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { ReportsAgentService } from '../../api/backendAiService';
import ReportCard from './ReportCard';
import DailyTokenCounter from './DailyTokenCounter';
import '../../styles/bookings-ai-agent.css';
import '../../styles/reports.css';

const EXAMPLES = [
  'Set a target',
  'How did we do last month compared with the month before? Show revenue by week.',
  'Set a $30,000 revenue target for December.',
  'Are we on track for this month\'s target?',
  'Which customers haven\'t visited in 3 months? Draft a New Year e-mail to them.',
  'Payroll for all staff for August.',
  'Top 10 services by revenue this year as a pie chart.',
];

const APPROVAL_RULES = [
  'Changing the default yearly / monthly / weekly / daily revenue targets',
  'Replacing or removing a target already set for a month, week or day',
  'Reports or exports containing customers\' e-mail addresses or phone numbers',
  'More than 5 changes from one instruction',
];

const TARGET_ACTIONS = ['set_default_targets', 'set_period_target', 'remove_period_target'];

function ReportsAIAgent({ salonId, onOpenTargets, onCreateCampaign }) {
  const [messages, setMessages] = useState([{
    id: 'welcome',
    type: 'ai',
    content: "Tell me what you'd like done in Reports Management - for example a revenue or customer report, a chart, checking progress against your targets, setting a target, payroll figures, or drafting an e-mail to the customers a report finds. I use the same rules as the Analytics Dashboard, Query Builder, Revenue Targets and Staff Payroll pages. Changing targets or exporting customers' contact details waits for your approval, and I never send anything to customers myself. You don't need to give every detail up front - I'll ask for whatever is missing.",
    timestamp: new Date()
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  // actionId -> { state: 'pending' | 'working' | 'done' | 'declined' | 'failed', message, report }
  const [approvals, setApprovals] = useState({});
  const serviceRef = useRef(null);
  const tokenCounterRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Approve/decline outcomes the agent hasn't been told about yet; sent along with the next instruction
  const unreportedOutcomes = useRef([]);

  useEffect(() => {
    if (salonId) serviceRef.current = new ReportsAgentService(salonId);
  }, [salonId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isLoading]);

  const addMessage = (msg) => setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, timestamp: new Date(), ...msg }]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading || limitReached || !serviceRef.current) return;

    addMessage({ type: 'user', content: text });
    setInput('');
    setIsLoading(true);

    const outcomes = unreportedOutcomes.current;
    unreportedOutcomes.current = [];
    const outgoing = outcomes.length
      ? `[Update since your last reply: ${outcomes.join(' ')}]\n\n${text}`
      : text;

    try {
      const res = await serviceRef.current.sendMessage(outgoing);
      setApprovals(prev => {
        const next = { ...prev };
        res.pendingActions.forEach(p => { next[p.actionId] = { state: 'pending' }; });
        return next;
      });
      addMessage({ type: 'ai', content: res.message, actions: res.actions, pendingActions: res.pendingActions, reports: res.reports, campaigns: res.campaigns });
      if (res.dailyUsage) tokenCounterRef.current?.update(res.dailyUsage);
    } catch (error) {
      console.error('Reports AI agent error:', error);
      unreportedOutcomes.current = [...outcomes, ...unreportedOutcomes.current];
      const content = error?.status === 429
        ? 'You have reached the daily AI usage limit. Reports AI is available again after midnight.'
        : error?.status === 403
          ? (error.message || 'Reports AI is not available for your account.')
          : "Something went wrong and I couldn't finish. Please check the Revenue Targets page before trying again - a change may already have been made.";
      addMessage({ type: 'system', content });
    } finally {
      setIsLoading(false);
      tokenCounterRef.current?.refresh();
    }
  };

  const handleDecision = async (action, decision) => {
    setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'working' } }));
    try {
      const res = await serviceRef.current.resolveAction(action.actionId, decision);
      if (res.declined) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'declined', message: 'Declined - nothing was changed.' } }));
        unreportedOutcomes.current.push(`The admin DECLINED "${action.title}", so it was not done.`);
      } else if (res.success) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'done', message: res.message, report: res.report } }));
        unreportedOutcomes.current.push(`The admin APPROVED "${action.title}" and it was completed: ${res.message}`);
      } else {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: res.message } }));
        unreportedOutcomes.current.push(`The admin approved "${action.title}" but it failed: ${res.message}`);
      }
    } catch (error) {
      setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: error.message } }));
    }
  };

  // Same hand-off the Promotions page uses: the Campaigns page picks this up and pre-fills its form
  const openCampaign = (campaign) => {
    const { type, subject, body, targetingType, targetingCriteria } = campaign;
    sessionStorage.setItem('pendingNotificationData', JSON.stringify({ type, subject, body, targetingType, targetingCriteria }));
    onCreateCampaign?.('communications', type);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (t) => t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const targetsLink = (types) => types.some(t => TARGET_ACTIONS.includes(t)) && onOpenTargets
    ? <button className="bai-link" onClick={onOpenTargets}>Open Revenue Targets →</button>
    : null;

  const renderApprovalCard = (p) => {
    const status = approvals[p.actionId] || { state: 'pending' };
    return (
      <div key={p.actionId} className={`bai-approval-card bai-approval-card--${status.state}`}>
        <div className="bai-approval-head">
          <span className="bai-approval-badge">Needs your approval</span>
          <strong>{p.title}</strong>
        </div>
        {p.lines?.map((line, i) => <div key={i} className="bai-approval-line">{line}</div>)}
        <div className="bai-approval-reason">{p.reason}</div>
        {(status.state === 'pending' || status.state === 'working') && (
          <div className="bai-approval-actions">
            <button className="bai-btn bai-btn--approve" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'approve')}>
              {status.state === 'working' ? 'Working…' : 'Approve'}
            </button>
            <button className="bai-btn bai-btn--decline" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'decline')}>
              Decline
            </button>
          </div>
        )}
        {status.state === 'done' && (
          <>
            <div className="bai-approval-result bai-approval-result--ok">✓ {status.message}</div>
            {targetsLink([p.type])}
            {status.report && <div className="message-report"><ReportCard reportData={status.report} /></div>}
          </>
        )}
        {status.state === 'declined' && <div className="bai-approval-result">{status.message}</div>}
        {status.state === 'failed' && <div className="bai-approval-result bai-approval-result--err">Not done: {status.message}</div>}
      </div>
    );
  };

  const renderCampaign = (c, i) => (
    <div key={i} className="bai-approval-card">
      <div className="bai-approval-head">
        <span className="bai-approval-badge">{c.type === 'email' ? 'E-mail draft' : 'Push draft'} - not sent</span>
        <strong>{c.subject}</strong>
      </div>
      <div className="bai-approval-line">To: {c.audienceLabel}</div>
      <div className="bai-approval-line" style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
      <div className="bai-approval-reason">Opens {c.type === 'email' ? 'Email' : 'Push Notification'} Campaigns with this filled in. Review it there and press Send when you're happy.</div>
      {onCreateCampaign && (
        <button className="bai-link" onClick={() => openCampaign(c)}>
          Open in {c.type === 'email' ? 'Email' : 'Push Notification'} Campaigns →
        </button>
      )}
    </div>
  );

  return (
    <div className="ai-tab-container bookings-ai-agent reports-ai-agent">
      <div className="ai-tab-header">
        <h2>Reports AI</h2>
        <p>Type an instruction and the assistant will carry it out in Reports Management - building reports and charts, checking and setting revenue targets, working out payroll and turning a report into a campaign draft, just like doing it by hand.</p>
      </div>

      <DailyTokenCounter ref={tokenCounterRef} salonId={salonId} onLimitReached={setLimitReached} />

      <div className="ai-interaction-panel">
        <div className="ai-prompt-examples">
          <h3>Try an instruction</h3>
          <div className="example-prompts">
            {EXAMPLES.map(ex => (
              <div key={ex} className="prompt-card" onClick={() => setInput(ex)}>"{ex}"</div>
            ))}
          </div>
          <div className="bai-rules">
            <h4>Always asks for your approval</h4>
            <ul>{APPROVAL_RULES.map(r => <li key={r}>{r}</li>)}</ul>
            <p>Nothing is sent to customers from here - campaign drafts open on the Campaigns page for you to send. Pay rates and rosters are changed in Working Hours.</p>
          </div>
        </div>

        <div className="ai-chat-interface">
          <div className="chat-messages">
            {messages.map(m => (
              <div key={m.id} className={`message ${m.type}-message`}>
                {m.type === 'ai' && <div className="ai-avatar">🤖</div>}
                {m.type === 'user' && <div className="user-avatar">👤</div>}
                {m.type === 'system' && <div className="system-avatar">ℹ️</div>}
                <div className="message-content">
                  <div className="message-text"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                  {m.reports?.map((r, i) => (
                    <div key={i} className="message-report"><ReportCard reportData={r} /></div>
                  ))}
                  {m.actions?.length > 0 && (
                    <div className="bai-done-list">
                      {m.actions.map((a, i) => (
                        <div key={i} className="bai-done-item">
                          <span className="bai-done-check">✓</span>
                          <span>{a.message}</span>
                        </div>
                      ))}
                      {targetsLink(m.actions.map(a => a.type))}
                    </div>
                  )}
                  {m.pendingActions?.map(renderApprovalCard)}
                  {m.campaigns?.map(renderCampaign)}
                  <div className="message-time">{formatTime(m.timestamp)}</div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={limitReached ? 'Daily AI usage limit reached. Resets at midnight.' : 'e.g. Set a $30,000 revenue target for December (Enter to send, Shift+Enter for a new line)'}
              rows="3"
              disabled={isLoading || limitReached}
            />
            <div className="input-actions">
              <span className="bai-hint">Target changes appear on the home page and Revenue Targets page immediately.</span>
              <button className="send-btn" onClick={handleSend} disabled={isLoading || !input.trim() || limitReached}>
                {isLoading ? 'Working…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportsAIAgent;
